import AutorenewIcon from '@mui/icons-material/Autorenew';
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { blue, grey, red, yellow } from '@mui/material/colors';
import { styled } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import ConfirmationDialog from '../ConfirmationDialog';
import DeploymentLogsModal, {
  formatLogsForDisplay,
} from '../DeploymentLogsModal';
import { useNodeStatus } from './hooks/useNodeStatus';
import { useValidation } from './hooks/useValidation';

const successColor = '#0cc421';
const editColor = grey[500];

const stateColors = {
  destroying: yellow[500],
  deploying: blue[500],
  not_deployed: '#9e9e9e',
  deployed: successColor,
  failed: '#f44336',
  deleting: yellow[500],
};

const NodeContainer = styled(Box)({
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
});

const StatusDot = styled('div')(({ theme, status }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  marginLeft: '6px',
  cursor: 'pointer',
  backgroundColor: (() => {
    switch (status) {
      case 'NOT_DEPLOYED':
        return stateColors.not_deployed;
      case 'DEPLOYING':
        return stateColors.deploying;
      case 'DESTROYING':
        return stateColors.destroying;
      case 'DEPLOYED':
        return stateColors.deployed;
      case 'FAILED':
        return stateColors.failed;
      case 'DELETING':
        return stateColors.deleting;
      default:
        return stateColors.not_deployed;
    }
  })(),
}));

const StyledIconButton = styled(IconButton)({
  cursor: 'pointer',
});

const STALE_DEPLOY_THRESHOLD_MIN = 45;

function deployStuckMessage(deployStatus, deployRequestedAt) {
  if (deployStatus !== 'DEPLOYING' && deployStatus !== 'DESTROYING') {
    return '';
  }
  if (!deployRequestedAt) {
    return '';
  }
  const t = new Date(deployRequestedAt).getTime();
  if (Number.isNaN(t)) {
    return '';
  }
  const ageMin = (Date.now() - t) / 60000;
  if (ageMin < STALE_DEPLOY_THRESHOLD_MIN) {
    return '';
  }
  return ` No progress for ${Math.floor(ageMin)}m — the API may mark this FAILED via reconcile, or retry after status updates.`;
}

const RotatingIcon = styled(AutorenewIcon)(({ theme, color }) => ({
  animation: 'spin 2s linear infinite',
  color: color,
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
}));

const NodeHeader = ({
  data,
  onOpenModal,
  onDeleteNode,
  edges = [],
  handleDeploy: parentHandleDeploy,
  handleDestroy: parentHandleDestroy,
  handleRun: parentHandleRun,
  handleRunNow: parentHandleRunNow,
  handleActivate: parentHandleActivate,
  handleDeactivate: parentHandleDeactivate,
  hasAgentGraph = false,
  destroyBlockedByDownstream = false,
}) => {
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [commandOutputs, setCommandOutputs] = useState(() =>
    formatLogsForDisplay(data.output_data)
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    setCommandOutputs(formatLogsForDisplay(data.output_data));
  }, [data.output_data]);

  const { status, isDeleting, _handleDeploy, _handleDestroy, handleDelete } =
    useNodeStatus(data.deploy_status);

  const {
    validationResult,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    validateNode,
    openConfirmDialog,
    closeConfirmDialog,
  } = useValidation();

  const handleOpenStatusModal = () => setIsStatusModalOpen(true);
  const handleCloseStatusModal = () => setIsStatusModalOpen(false);

  const isAgentNode =
    typeof data.type === 'string' && data.type.startsWith('agent/');
  const useRunUntil = Boolean(parentHandleRun) && (isAgentNode || hasAgentGraph);
  const runOrDeploy = useRunUntil ? parentHandleRun : parentHandleDeploy;

  const handleDeployClick = useCallback(async () => {
    const isValid = validateNode(data, edges);
    if (isValid) {
      try {
        await runOrDeploy();
      } catch (error) {
        setError(
          useRunUntil
            ? 'Failed to run. Please try again.'
            : 'Failed to deploy. Please try again.'
        );
        console.error(useRunUntil ? 'Run failed:' : 'Deployment failed:', error);
      }
    } else {
      openConfirmDialog();
    }
  }, [
    data,
    edges,
    validateNode,
    runOrDeploy,
    useRunUntil,
    openConfirmDialog,
  ]);

  const handleConfirmDeploy = useCallback(async () => {
    closeConfirmDialog();
    try {
      await runOrDeploy();
    } catch (error) {
      setError(
        useRunUntil
          ? 'Failed to run. Please try again.'
          : 'Failed to deploy. Please try again.'
      );
      console.error(useRunUntil ? 'Run failed:' : 'Deployment failed:', error);
    }
  }, [closeConfirmDialog, runOrDeploy, useRunUntil]);

  const handleDestroyClick = useCallback(async () => {
    if (destroyBlockedByDownstream) return;
    try {
      await parentHandleDestroy();
    } catch (error) {
      setError('Failed to destroy. Please try again.');
      console.error('Destruction failed:', error);
    }
  }, [parentHandleDestroy, destroyBlockedByDownstream]);

  const handleDeleteClick = useCallback(async () => {
    try {
      await handleDelete(onDeleteNode, data.id);
    } catch (error) {
      setError('Failed to delete. Please try again.');
      console.error('Deletion failed:', error);
    }
  }, [handleDelete, onDeleteNode, data.id]);

  const renderActionButton = () => {
    if (isDeleting) {
      return (
        <Tooltip title="Deleting">
          <span>
            <StyledIconButton
              size="small"
              sx={{ ml: 0.5, p: 0.5, color: red[500] }}
              disabled
            >
              <RotatingIcon fontSize="small" color={red[500]} />
            </StyledIconButton>
          </span>
        </Tooltip>
      );
    }

    switch (status) {
      case 'NOT_DEPLOYED':
      case 'FAILED':
        return (
          <Tooltip title={useRunUntil ? 'Run' : 'Deploy'}>
            <StyledIconButton
              size="small"
              onClick={handleDeployClick}
              sx={{ ml: 0.5, p: 0.5, color: stateColors.deployed }}
            >
              <PlayArrowIcon fontSize="small" />
            </StyledIconButton>
          </Tooltip>
        );
      case 'DEPLOYED':
        return (
          <>
            {useRunUntil && (
              <Tooltip title="Run">
                <StyledIconButton
                  size="small"
                  onClick={handleDeployClick}
                  sx={{ ml: 0.5, p: 0.5, color: stateColors.deployed }}
                >
                  <PlayArrowIcon fontSize="small" />
                </StyledIconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                destroyBlockedByDownstream
                  ? 'Deprovision downstream packages first (all must be NOT_DEPLOYED)'
                  : 'Destroy'
              }
            >
              <span>
                <StyledIconButton
                  size="small"
                  onClick={handleDestroyClick}
                  sx={{ ml: 0.5, p: 0.5, color: stateColors.destroying }}
                  disabled={destroyBlockedByDownstream}
                >
                  <StopIcon fontSize="small" />
                </StyledIconButton>
              </span>
            </Tooltip>
          </>
        );
      case 'DESTROYING':
      case 'DEPLOYING':
        return (
          <Tooltip
            title={`${
              status === 'DESTROYING' ? 'Destroying' : 'Deploying'
            }${deployStuckMessage(status, data.deploy_requested_at)}`}
          >
            <span>
              <StyledIconButton
                size="small"
                sx={{ ml: 0.5, p: 0.5, color: stateColors.deployed }}
                disabled
              >
                <RotatingIcon fontSize="small" color={stateColors.deployed} />
              </StyledIconButton>
            </span>
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <NodeContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" key={data.label}>
          {data.label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip
            title={`Logs${deployStuckMessage(
              isDeleting ? 'DELETING' : status,
              data.deploy_requested_at
            )}`}
          >
            <StatusDot
              status={isDeleting ? 'DELETING' : status}
              onClick={handleOpenStatusModal}
            />
          </Tooltip>
          <Tooltip
            title={
              status === 'DEPLOYING' || status === 'DESTROYING'
                ? 'Unavailable while deploying or destroying'
                : 'Edit'
            }
          >
            <span>
              <StyledIconButton
                size="small"
                onClick={onOpenModal}
                sx={{ ml: 0.5, p: 0.5, color: editColor }}
                disabled={
                  isDeleting ||
                  status === 'DEPLOYING' ||
                  status === 'DESTROYING'
                }
              >
                <EditIcon fontSize="small" />
              </StyledIconButton>
            </span>
          </Tooltip>
          {data.type === 'workflow/script' && parentHandleRunNow && (
            <Tooltip title="Run now">
              <StyledIconButton
                size="small"
                onClick={parentHandleRunNow}
                sx={{ ml: 0.5, p: 0.5, color: stateColors.deployed }}
                disabled={
                  isDeleting ||
                  status === 'DEPLOYING' ||
                  status === 'DESTROYING'
                }
              >
                <BoltIcon fontSize="small" />
              </StyledIconButton>
            </Tooltip>
          )}
          {data.type === 'workflow/timer' &&
            (parentHandleActivate || parentHandleDeactivate) && (
              <Tooltip
                title={
                  data.parameter_data?.active
                    ? 'Deactivate schedule'
                    : 'Activate schedule'
                }
              >
                <StyledIconButton
                  size="small"
                  onClick={
                    data.parameter_data?.active
                      ? parentHandleDeactivate
                      : parentHandleActivate
                  }
                  sx={{
                    ml: 0.5,
                    p: 0.5,
                    color: data.parameter_data?.active
                      ? stateColors.deployed
                      : stateColors.not_deployed,
                  }}
                  disabled={isDeleting}
                >
                  {data.parameter_data?.active ? (
                    <ToggleOnIcon fontSize="small" />
                  ) : (
                    <ToggleOffIcon fontSize="small" />
                  )}
                </StyledIconButton>
              </Tooltip>
            )}
          {renderActionButton()}
          <Tooltip
            title={
              status === 'DEPLOYING' || status === 'DESTROYING'
                ? 'Unavailable while deploying or destroying'
                : 'Delete (downstream nodes, then connections, then this card)'
            }
          >
            <span>
              <StyledIconButton
                size="small"
                onClick={handleDeleteClick}
                sx={{ ml: 0.5, p: 0.5, color: red[500] }}
                disabled={
                  isDeleting ||
                  status === 'DEPLOYING' ||
                  status === 'DESTROYING'
                }
              >
                <DeleteIcon fontSize="small" />
              </StyledIconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <DeploymentLogsModal
        isOpen={isStatusModalOpen}
        onClose={handleCloseStatusModal}
        logs={commandOutputs}
      />
      <ConfirmationDialog
        open={isConfirmDialogOpen}
        title="Warning: Missing Connections"
        content={`This package is missing the following connections:
          ${
            validationResult?.missingInputs.length
              ? `\nInputs: ${validationResult.missingInputs.join(', ')}`
              : ''
          }
          ${
            validationResult?.missingOutputs.length
              ? `\nOutputs: ${validationResult.missingOutputs.join(', ')}`
              : ''
          }
          \nDo you want to proceed with ${
            useRunUntil ? 'the run' : 'deployment'
          } anyway?`}
        onConfirm={handleConfirmDeploy}
        onCancel={() => setIsConfirmDialogOpen(false)}
      />
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </NodeContainer>
  );
};

export default React.memo(NodeHeader);
